import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../../lib/api";
import { maskCpfCnpj, validateCpfCnpj } from "../../lib/cpf";
import { toast } from "sonner";
import { Trash, Key, ToggleLeft, ToggleRight, UserCircle, CreditCard } from "@phosphor-icons/react";

export default function AdminResellers() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", cpf_cnpj: "" });
  const [pwModal, setPwModal] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [mpModal, setMpModal] = useState(null);
  const [mpCfg, setMpCfg] = useState(null);
  const [mpToken, setMpToken] = useState("");
  const [mpBusy, setMpBusy] = useState("");

  const load = async () => setItems((await api.get("/users/resellers")).data);
  useEffect(() => {
    (async () => { try { setItems((await api.get("/users/resellers")).data); } catch { /* ignore */ } })();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.cpf_cnpj && form.cpf_cnpj.replace(/\D/g, "").length > 0) {
      const { ok, isCnpj } = validateCpfCnpj(form.cpf_cnpj);
      if (!ok) { toast.error(`${isCnpj ? "CNPJ" : "CPF"} inválido. Verifique os números informados.`); return; }
    }
    try {
      await api.post("/users/resellers", { ...form, cpf_cnpj: form.cpf_cnpj || undefined, role: "reseller" });
      toast.success("Revendedora cadastrada");
      setForm({ name: "", email: "", password: "", cpf_cnpj: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: u.is_active === false });
      toast.success(u.is_active === false ? "Ativada" : "Desativada");
      load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir revendedora ${u.name}? Esta acao nao pode ser desfeita.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Revendedora excluida");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const savePassword = async () => {
    if (!newPw || newPw.length < 6) return toast.error("Senha minima de 6 caracteres");
    try {
      await api.patch(`/users/${pwModal.id}`, { password: newPw });
      toast.success("Senha redefinida");
      setPwModal(null); setNewPw("");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const openMp = async (u) => {
    setMpModal(u); setMpToken(""); setMpCfg(null);
    try { setMpCfg((await api.get(`/integrations/reseller/${u.id}`)).data); } catch { toast.error("Erro ao carregar"); }
  };
  const reloadMp = async () => { try { setMpCfg((await api.get(`/integrations/reseller/${mpModal.id}`)).data); } catch { /* ignore */ } };
  const mpRun = async (key, fn, ok) => {
    setMpBusy(key);
    try { await fn(); if (ok) toast.success(ok); await reloadMp(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setMpBusy(""); }
  };
  const saveMp = () => mpRun("save", async () => { await api.put(`/integrations/reseller/${mpModal.id}`, { access_token: mpToken || undefined }); setMpToken(""); }, "Token salvo");
  const testMp = () => mpRun("test", async () => { const { data } = await api.post(`/integrations/reseller/${mpModal.id}/test`, { token: mpToken || undefined }); data.valid ? toast.success(data.message) : toast.error(data.message); });
  const toggleMp = () => mpRun("toggle", async () => { await api.put(`/integrations/reseller/${mpModal.id}`, { enabled: !mpCfg.enabled }); });

  return (
    <div data-testid="admin-resellers-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Time de vendas</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Revendedoras</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 table-wrap">
          <table className="crl">
            <thead><tr><th>Nome</th><th>E-mail</th><th>Cadastrada</th><th>Status</th><th>Acoes</th></tr></thead>
            <tbody>
              {items.map((u) => {
                const active = u.is_active !== false;
                return (
                <tr key={u.id} data-testid={`reseller-row-${u.id}`}>
                  <td className="font-medium"><Link to={`/admin/revendedoras/${u.id}`} className="hover:underline" data-testid={`reseller-link-${u.id}`}>{u.name}</Link></td>
                  <td className="text-[#5C5A57]">{u.email}</td>
                  <td className="text-[#5C5A57] text-xs">{formatDate(u.created_at)}</td>
                  <td>
                    <span className={`badge ${active ? "green" : "red"}`}>{active ? "Ativa" : "Inativa"}</span>
                  </td>
                  <td>
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleActive(u)} className="text-[#5C5A57] hover:text-black" title={active ? "Desativar" : "Ativar"} data-testid={`toggle-${u.id}`}>
                        {active ? <ToggleRight size={22} weight="fill" /> : <ToggleLeft size={22} />}
                      </button>
                      <button onClick={() => { setPwModal(u); setNewPw(""); }} className="text-[#5C5A57] hover:text-black" title="Trocar senha" data-testid={`pwd-${u.id}`}>
                        <Key size={18} />
                      </button>
                      <button onClick={() => openMp(u)} className="text-[#5C5A57] hover:text-black" title="Mercado Pago (PIX)" data-testid={`mp-${u.id}`}>
                        <CreditCard size={18} />
                      </button>
                      <button onClick={() => remove(u)} className="text-[#8B5E5E] hover:text-[#5C2D2B]" title="Excluir" data-testid={`del-${u.id}`}>
                        <Trash size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
              {items.length === 0 && <tr><td colSpan="5" className="text-center text-sm text-[#5C5A57] py-8">Nenhuma revendedora ainda.</td></tr>}
            </tbody>
          </table>
        </div>

        <form onSubmit={create} className="border border-[#EBE5DF] bg-[#F5F0EB] p-6">
          <h2 className="section-title mb-6">Cadastrar nova</h2>
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nome</label>
              <input className="input-underline" data-testid="reseller-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">E-mail</label>
              <input className="input-underline" data-testid="reseller-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CPF / CNPJ</label>
              <input className="input-underline" data-testid="reseller-cpf-input" value={form.cpf_cnpj} onChange={(e) => setForm({ ...form, cpf_cnpj: maskCpfCnpj(e.target.value) })} placeholder="000.000.000-00" inputMode="numeric" maxLength={18} />
              {form.cpf_cnpj && [11, 14].includes(form.cpf_cnpj.replace(/\D/g, "").length) && !validateCpfCnpj(form.cpf_cnpj).ok && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="reseller-cpf-error">{form.cpf_cnpj.replace(/\D/g, "").length === 14 ? "CNPJ inválido. Verifique os números informados." : "CPF inválido. Verifique os números informados."}</div>}
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Senha inicial</label>
              <input className="input-underline" data-testid="reseller-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
            </div>
            <button type="submit" className="brand mt-3" data-testid="reseller-create-btn">Cadastrar Revendedora</button>
          </div>
        </form>
      </div>

      {mpModal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" onClick={() => setMpModal(null)}>
          <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="reseller-mp-modal">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={20} weight="duotone" />
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Mercado Pago (PIX) de</div>
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-1">{mpModal.name}</h2>
            <div className="text-xs text-[#5C5A57] mb-5">Usado quando o modo PIX e Por revendedora.</div>
            {mpCfg ? (
              <>
                <div className="mb-3 text-xs text-[#5C5A57]">
                  Status: <strong>{mpCfg.status}</strong>{mpCfg.configured && <> · Token: <strong className="tabular">{mpCfg.masked}</strong></>}
                </div>
                <input type="password" autoComplete="off" value={mpToken} onChange={(e) => setMpToken(e.target.value)} className="input-underline" placeholder="APP_USR-... ou TEST-..." data-testid="admin-reseller-mp-input" />
                <div className="flex flex-wrap gap-3 mt-5">
                  <button onClick={saveMp} disabled={mpBusy === "save"} className="brand" data-testid="admin-reseller-mp-save">{mpBusy === "save" ? "..." : "Salvar"}</button>
                  <button onClick={testMp} disabled={mpBusy === "test"} className="brand" style={{ background: "#5C5A57" }} data-testid="admin-reseller-mp-test">{mpBusy === "test" ? "..." : "Testar"}</button>
                  <button onClick={toggleMp} disabled={mpBusy === "toggle" || !mpCfg.configured} className="flex items-center gap-2 text-xs tracking-widest uppercase font-medium hover:underline disabled:opacity-40" data-testid="admin-reseller-mp-toggle">
                    {mpCfg.enabled ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />}{mpCfg.enabled ? "Ativado" : "Desativado"}
                  </button>
                </div>
              </>
            ) : <div className="text-sm text-[#5C5A57]">Carregando...</div>}
            <div className="flex justify-end mt-6">
              <button onClick={() => setMpModal(null)} className="text-xs tracking-widest uppercase text-[#5C5A57] hover:underline">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {pwModal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" onClick={() => setPwModal(null)}>
          <div className="bg-white border border-[#EBE5DF] max-w-sm w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Key size={20} weight="duotone" />
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Nova senha para</div>
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{pwModal.name}</h2>
            <input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="input-underline" placeholder="Nova senha (min 6)" autoFocus data-testid="new-password-input" />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setPwModal(null)} className="text-xs tracking-widest uppercase text-[#5C5A57] hover:underline">Cancelar</button>
              <button onClick={savePassword} className="brand" data-testid="save-password-btn">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
